/**
 * React Router configuration for the application.
 * Uses lazy loading for better performance and code splitting.
 */

import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { AppLayout } from './components/layout/AppLayout';

// Lazy load pages for code splitting
const LoginPage = lazy(() => import('./pages/LoginPage').then(m => ({ default: m.LoginPage })));
const SignupPage = lazy(() => import('./pages/SignupPage').then(m => ({ default: m.SignupPage })));
const OTPVerificationPage = lazy(() => import('./pages/OTPVerificationPage').then(m => ({ default: m.OTPVerificationPage })));
const DashboardPage = lazy(() => import('./pages/DashboardPage').then(m => ({ default: m.DashboardPage })));
const PatientsPage = lazy(() => import('./pages/PatientsPage').then(m => ({ default: m.PatientsPage })));
const PatientDetailPage = lazy(() => import('./pages/PatientDetailPage').then(m => ({ default: m.PatientDetailPage })));
const EuroScorePage = lazy(() => import('./pages/EuroScorePage').then(m => ({ default: m.EuroScorePage })));
const STSScorePage = lazy(() => import('./pages/STSScorePage').then(m => ({ default: m.STSScorePage })));
const ComparisonPage = lazy(() => import('./pages/ComparisonPage').then(m => ({ default: m.ComparisonPage })));
const SettingsPage = lazy(() => import('./pages/SettingsPage').then(m => ({ default: m.SettingsPage })));
const ReportReviewPage = lazy(() => import('./pages/ReportReviewPage').then(m => ({ default: m.ReportReviewPage })));
const ReportListPage = lazy(() => import('./pages/ReportListPage').then(m => ({ default: m.ReportListPage })));

// Loading fallback component
const PageLoader = () => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    fontSize: '1.125rem',
    color: '#6b7280'
  }}>
    Loading...
  </div>
);

// Wrap lazy components with Suspense
const withSuspense = (Component: React.LazyExoticComponent<any>) => (
  <Suspense fallback={<PageLoader />}>
    <Component />
  </Suspense>
);

export const router = createBrowserRouter([
  // Auth routes (public)
  {
    path: '/login',
    element: withSuspense(LoginPage),
  },
  {
    path: '/auth/signup',
    element: withSuspense(SignupPage),
  },
  {
    path: '/auth/verify-otp',
    element: withSuspense(OTPVerificationPage),
  },
  // Protected routes
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <AppLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <Navigate to="/login" replace />,
      },
      {
        path: 'dashboard',
        element: withSuspense(DashboardPage),
      },
      {
        path: 'patients',
        element: withSuspense(PatientsPage),
      },
      {
        path: 'patients/:patientId',
        element: withSuspense(PatientDetailPage),
      },
      {
        path: 'patients/:patientId/euroscore',
        element: withSuspense(EuroScorePage),
      },
      {
        path: 'patients/:patientId/sts',
        element: withSuspense(STSScorePage),
      },
      {
        path: 'patients/:patientId/compare',
        element: withSuspense(ComparisonPage),
      },
      {
        path: 'reports',
        element: (
          <ProtectedRoute requiredRoles={['clinician', 'admin']}>
            <Suspense fallback={<PageLoader />}>
              <ReportListPage />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'reports/:reportId/extract',
        element: (
          <ProtectedRoute requiredRoles={['clinician', 'admin']}>
            <Suspense fallback={<PageLoader />}>
              <ReportReviewPage />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'settings',
        element: withSuspense(SettingsPage),
      },
    ],
  },
  {
    path: '*',
    element: <Navigate to="/login" replace />,
  },
]);
